import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { MedicationEvent, ScheduleCalendarEvent } from '../types';
import { Status, mapApiStatusToEnum } from '../utils/statusUtils';
import { dimGrey } from '../../../../../../mainTheme';

export const eventTooltipLabel = (event: MedicationEvent): string =>
  `${event.title} (${moment(event.start).format('h:mm A')})`;

// forwardRef so the Tooltip wrapping it can anchor to the block
const OverflowBlock = React.forwardRef<HTMLDivElement, { count: number }>(
  ({ count, ...tooltipProps }, ref) => (
    <Box
      ref={ref}
      {...tooltipProps}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        borderRadius: '4px',
        background: '#F6F6F6',
        boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.05) inset',
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          color: '#444444',
          letterSpacing: 0,
        }}
      >
        {`+${count}`}
      </Typography>
    </Box>
  ),
);

OverflowBlock.displayName = 'OverflowBlock';

export const EventComponent = React.memo(
  ({ event }: { event: ScheduleCalendarEvent }) => {
    const medEvent = event.medEvent;

    if (!medEvent) {
      const overflowCount = event.overflowEvents.length;

      return (
        <Tooltip
          title={`${overflowCount} more medication ${
            overflowCount === 1 ? 'event' : 'events'
          }`}
          placement="top"
          arrow
        >
          <OverflowBlock count={overflowCount} />
        </Tooltip>
      );
    }

    // Determine if event is in the past (end time < now)
    const isPast = moment(medEvent.end).isBefore(moment());
    const eventCardColor = isPast ? '#A0A0A0' : '#444444';

    // Get event status for icon display
    const eventStatus = mapApiStatusToEnum(medEvent.status);

    return (
      <Tooltip title={eventTooltipLabel(medEvent)} placement="top" arrow>
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            height: '100%',
            width: '100%',
            borderRadius: '4px',
            // clip the accent bar and fill to one radius; as a border-left the
            // bar rounds elliptically, since a border's inner radius is the
            // outer radius minus that side's width, per axis
            overflow: 'hidden',
            background: '#F6F6F6',
            boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.05) inset',
          }}
        >
          <Box
            sx={{ width: '8px', flexShrink: 0, background: eventCardColor }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '100%',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {/* Event title */}
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                color: eventCardColor,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                letterSpacing: 0,
                ml: '4px',
              }}
            >
              {medEvent.title}
            </Typography>
            {/* Event time */}
            {/* minWidth 0 so a narrow block ellipsizes the time instead of
              clipping it mid-digit against the card's overflow */}
            <Box display="flex" gap="4px" px="4px" minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: eventCardColor,
                  textAlign: 'right',
                  letterSpacing: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                }}
              >
                {moment(medEvent.start).format('h:mm')}
              </Typography>
              {/* Status icon */}
              {eventStatus === Status.MISSED && (
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{
                    fontSize: 14,
                    color: eventCardColor,
                  }}
                />
              )}
              {eventStatus === Status.COMPLETE && (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  style={{
                    fontSize: 14,
                    color: eventCardColor,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Tooltip>
    );
  },
);

EventComponent.displayName = 'EventComponent';

export const DayHeader = React.memo(({ date }: { date: Date }) => {
  const isToday = moment(date).isSame(moment(), 'day');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        pointerEvents: 'none',
        cursor: 'default',
        userSelect: 'none',
        px: 0,
        py: 0,
        m: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          px: 2,
          py: 0.5,
          backgroundColor: isToday ? '#D5C6DC' : 'transparent',
          transition: 'background 0.2s',
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 500,
            color: isToday ? '#000' : dimGrey,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            textAlign: 'center',
          }}
        >
          {moment(date).format('D')}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: isToday ? '#000' : dimGrey,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            textAlign: 'center',
            mt: 0,
          }}
        >
          {moment(date).format('dddd')}
        </Typography>
      </Box>
    </Box>
  );
});

DayHeader.displayName = 'DayHeader';

export const TimeHeaderGutter = React.memo(() => {
  // Get browser's timezone to show on main calendar view
  const tzAbbr = moment.tz(moment.tz.guess()).format('z');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 500,
          color: dimGrey,
          lineHeight: '24px',
          letterSpacing: '0.2px',
          textAlign: 'center',
        }}
      >
        {tzAbbr}
      </Typography>
    </Box>
  );
});

TimeHeaderGutter.displayName = 'TimeHeaderGutter';
